import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Printer, Download, FolderKanban, ArrowLeft, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { writePrintDoc } from '../../utils/printUtils';
import { useAuth } from '../../AuthContext';
import ViewBillModal from '../../components/ViewBillModal';

const ProjectExpenditureReport = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const canViewAll = isAdmin() || ['Checker', 'Account', 'HR'].includes(user?.role);

  const [activities, setActivities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState({ grand_total: 0, activities: [] });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    branch_id: 'all',
    project_activity_id: 'all',
  });

  const [viewBill, setViewBill] = useState({ open: false, id: null, filename: null });

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get('/project-activities'),
          canViewAll ? api.get('/branches') : Promise.resolve({ data: [] }),
        ]);
        setActivities(a.data || []);
        setBranches(b.data || []);
      } catch {
        toast.error('Failed to load filters');
      }
    })();
    fetchReport();
    // eslint-disable-next-line
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.branch_id && filters.branch_id !== 'all') params.branch_id = filters.branch_id;
      if (filters.project_activity_id && filters.project_activity_id !== 'all') params.project_activity_id = filters.project_activity_id;
      const r = await api.get('/expenditures/reports/project-wise', { params });
      setData(r.data || { grand_total: 0, activities: [] });
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handlePrint = () => {
    const rangeText = (filters.date_from || filters.date_to)
      ? `${filters.date_from || '…'} to ${filters.date_to || '…'}`
      : 'All time';
    const activityName =
      filters.project_activity_id === 'all'
        ? 'All Project Activities'
        : (activities.find((a) => a.id === filters.project_activity_id)?.name || '-');
    const branchName =
      filters.branch_id === 'all'
        ? 'All Blocks'
        : (branches.find((b) => b.id === filters.branch_id)?.name || '-');

    const rowsHtml = data.activities.map((pa) => {
      const blocksHtml = pa.blocks.map((blk) => {
        const entriesHtml = blk.entries.map((e) => `
          <tr>
            <td>${e.date ? new Date(e.date).toLocaleDateString('en-IN') : '-'}</td>
            <td>${e.voucher_number || '-'}</td>
            <td>${e.expenditure_head || '-'}</td>
            <td>${(e.description || '-').replace(/</g, '&lt;')}</td>
            <td class="amount">Rs.${e.amount.toLocaleString('en-IN')}</td>
          </tr>
        `).join('');
        return `
          <tr class="block-row"><td colspan="5">Block: <b>${blk.branch_name}</b> — Sub-total: Rs.${blk.total.toLocaleString('en-IN')}</td></tr>
          ${entriesHtml}
        `;
      }).join('');
      return `
        <tr class="pa-row"><td colspan="5">Project Activity: <b>${pa.activity_name}</b> — Total: Rs.${pa.total.toLocaleString('en-IN')}</td></tr>
        ${blocksHtml}
      `;
    }).join('');

    const html = `
      <html><head><title>Project-wise Expenditure Report — WWAGS MIS</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#111}
        h1{color:#047857;font-size:20px;margin:0 0 4px}
        .meta{color:#555;font-size:11px;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th{background:#047857;color:#fff;padding:8px;text-align:left}
        td{padding:6px 8px;border-bottom:1px solid #ddd;vertical-align:top}
        .amount{text-align:right;font-weight:600}
        .pa-row td{background:#ecfdf5;color:#065f46;font-size:12px}
        .block-row td{background:#f1f5f9;color:#334155;padding-left:16px}
        .grand{background:#fff7ed;font-weight:700;font-size:12px}
        @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style>
      </head><body>
      <h1>WWAGS MIS — Project-wise Expenditure Report</h1>
      <div class="meta">
        Generated: ${new Date().toLocaleString('en-IN')} &nbsp; | &nbsp;
        Range: ${rangeText} &nbsp; | &nbsp;
        Activity: ${activityName} &nbsp; | &nbsp;
        Block: ${branchName}
      </div>
      <table>
        <thead><tr><th>Date</th><th>Voucher</th><th>Expenditure Head</th><th>Description</th><th class="amount">Amount</th></tr></thead>
        <tbody>
          ${rowsHtml || '<tr><td colspan="5" style="text-align:center;padding:20px">No records found</td></tr>'}
          <tr class="grand"><td colspan="4" style="text-align:right">Grand Total:</td><td class="amount">Rs.${data.grand_total.toLocaleString('en-IN')}</td></tr>
        </tbody>
      </table>
      </body></html>
    `;
    const pw = window.open('', '_blank');
    if (pw) { writePrintDoc(pw.document, html); setTimeout(() => pw.print(), 500); }
  };

  const downloadCsv = () => {
    const lines = ['Project Activity,Block,Date,Voucher,Expenditure Head,Description,Amount'];
    data.activities.forEach((pa) => {
      pa.blocks.forEach((blk) => {
        blk.entries.forEach((e) => {
          const desc = (e.description || '').replace(/"/g, '""');
          lines.push(`"${pa.activity_name}","${blk.branch_name}","${e.date ? new Date(e.date).toLocaleDateString('en-IN') : ''}","${e.voucher_number || ''}","${e.expenditure_head || ''}","${desc}",${e.amount}`);
        });
      });
    });
    lines.push(`,,,,,,${data.grand_total}`);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-wise-expenditure-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="project-expenditure-report">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
        </Button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-1 flex items-center gap-3">
              <FolderKanban className="w-8 h-8 text-emerald-700" />
              Project-wise Expenditure Report
            </h1>
            <p className="text-lg text-slate-600">Expenditure grouped by Project Activity and Block</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadCsv} data-testid="download-csv-button">
              <Download className="w-4 h-4 mr-2" /> Download CSV
            </Button>
            <Button onClick={handlePrint} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="print-report-button">
              <Printer className="w-4 h-4 mr-2" /> Print Report
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <Label htmlFor="from">From Date</Label>
            <Input id="from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
          </div>
          <div>
            <Label htmlFor="to">To Date</Label>
            <Input id="to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
          </div>
          <div>
            <Label>Project Activity</Label>
            <Select value={filters.project_activity_id} onValueChange={(v) => setFilters({ ...filters, project_activity_id: v })}>
              <SelectTrigger data-testid="filter-activity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                {activities.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canViewAll && (
            <div>
              <Label>Block</Label>
              <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v })}>
                <SelectTrigger data-testid="filter-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Blocks</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={fetchReport} className="bg-emerald-700 hover:bg-emerald-800 text-white h-10" data-testid="apply-filter-button">
            Apply Filter
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
            <div className="text-sm text-slate-600">Grand Total</div>
            <div className="text-2xl font-bold text-orange-700" data-testid="grand-total">
              Rs.{data.grand_total.toLocaleString('en-IN')}
            </div>
          </div>

          {data.activities.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="no-records">
              No expenditure records match the filters
            </div>
          ) : (
            <div className="overflow-auto">
              {data.activities.map((pa) => (
                <div key={pa.project_activity_id} className="border-b border-slate-200" data-testid="activity-group">
                  <div className="bg-emerald-50 border-l-4 border-emerald-600 px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Project Activity</div>
                      <div className="text-base font-bold text-emerald-900">{pa.activity_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-emerald-700">Total</div>
                      <div className="text-lg font-bold text-emerald-800">Rs.{pa.total.toLocaleString('en-IN')}</div>
                    </div>
                  </div>

                  {pa.blocks.map((blk) => (
                    <div key={blk.branch_id}>
                      <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-700">Block: {blk.branch_name}</div>
                        <div className="text-sm font-semibold text-slate-800">Rs.{blk.total.toLocaleString('en-IN')}</div>
                      </div>
                      <table className="w-full">
                        <thead className="bg-white border-b border-slate-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Voucher</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Expenditure Head</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Description</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                            <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase">Bill</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blk.entries.map((e) => (
                            <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="px-4 py-2 text-sm text-slate-700">{e.date ? new Date(e.date).toLocaleDateString('en-IN') : '-'}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{e.voucher_number}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{e.expenditure_head}</td>
                              <td className="px-4 py-2 text-sm text-slate-600">{e.description || '-'}</td>
                              <td className="px-4 py-2 text-right text-sm font-semibold text-slate-900">Rs.{e.amount.toLocaleString('en-IN')}</td>
                              <td className="px-4 py-2 text-center">
                                {e.has_bill ? (
                                  <button
                                    type="button"
                                    onClick={() => setViewBill({ open: true, id: e.id, filename: e.bill_filename })}
                                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 hover:underline font-medium"
                                    data-testid={`report-view-bill-${e.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ViewBillModal
        open={viewBill.open}
        onOpenChange={(o) => setViewBill({ open: o, id: o ? viewBill.id : null, filename: o ? viewBill.filename : null })}
        expenditureId={viewBill.id}
        filename={viewBill.filename}
      />
    </div>
  );
};

export default ProjectExpenditureReport;
